import { INVESTOR_ROUTES, portfolioDetailPath } from './constants/routesConst';
import { INVESTOR_VIEW_REPORT } from './constants/mixpanelEvents/investorEvents';
import { Mixpanel } from './mixpanel';
import { setMobileOverviewSource } from '../store/investor/action';

export const INVESTOR_DEALS_PATH = `${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DEALS}`;

export const COMPANY_REPORT_ORIGIN = { LEADS: 'leads', DEALS: 'deals' } as const;

export type CompanyReportOrigin = (typeof COMPANY_REPORT_ORIGIN)[keyof typeof COMPANY_REPORT_ORIGIN];

type CompanyReportLinkInput = {
    orgId: string;
    origin: CompanyReportOrigin;
    isPartner: boolean;
    search: string;
};

type CompanyReportLink = {
    path: string;
    trackViewReport: boolean;
};

type ReportViewer = { userId?: string; accountType?: string } | null | undefined;

const withQuery = (path: string, query: string) => (query ? `${path}?${query}` : path);

const getDealsQuery = (search: string, isPartner: boolean) => {
    const params = new URLSearchParams(search);
    params.delete('lenderId');
    if (isPartner) params.set('source', COMPANY_REPORT_ORIGIN.DEALS);
    return params.toString();
};

export const getCompanyReportLink = ({ orgId, origin, isPartner, search }: CompanyReportLinkInput): CompanyReportLink => {
    const isLeads = origin === COMPANY_REPORT_ORIGIN.LEADS;

    if (isLeads && isPartner) {
        return {
            path: `${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DATA_ROOM}/${orgId}?tab=dataRoom`,
            trackViewReport: false,
        };
    }

    if (isLeads) {
        return {
            path: withQuery(portfolioDetailPath(orgId), new URLSearchParams(search).toString()),
            trackViewReport: true,
        };
    }

    const basePath = isPartner ? `${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DATA_ROOM}/${orgId}` : portfolioDetailPath(orgId);
    return {
        path: withQuery(basePath, getDealsQuery(search, isPartner)),
        trackViewReport: false,
    };
};

export const openCompanyReportInNewTab = (link: CompanyReportLink, userData: ReportViewer) => {
    window.open(link.path, '_blank', 'noopener,noreferrer');
    if (link.trackViewReport) {
        Mixpanel.track(INVESTOR_VIEW_REPORT, { user_id: userData?.userId, user_type: userData?.accountType });
    }
};

export const openCompanyOnMobile = (
    origin: CompanyReportOrigin,
    orgId: string,
    dispatch: (action: ReturnType<typeof setMobileOverviewSource>) => unknown,
    navigate: (path: string) => void,
) => {
    dispatch(setMobileOverviewSource(origin === COMPANY_REPORT_ORIGIN.LEADS ? INVESTOR_ROUTES.APP_SCAN : INVESTOR_DEALS_PATH));
    navigate(portfolioDetailPath(orgId));
};

const LEADS_LISTING_PATHS = [INVESTOR_ROUTES.APP_SCAN, INVESTOR_ROUTES.APP_WATCH, `${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.MONITOR}`];

export const isOnListingOf = (pathname: string, origin: CompanyReportOrigin) =>
    origin === COMPANY_REPORT_ORIGIN.LEADS ? LEADS_LISTING_PATHS.includes(pathname) : pathname === INVESTOR_DEALS_PATH;

export const isOnCompanyRoute = (pathname: string, orgId: string) => pathname.split('/').includes(orgId);
